import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import GlobalExportButtons from '../GlobalExportButtons';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { formatCurrencyIntl } from '../../utils/currencyUtils';
import { CollapsibleQuickTotals, ReportHeaderWithExport } from './CommonComponents';

const ProfitLossReport = ({ db, userId, appId, companyId, dateRange, financialYear, selectedParty, parties, loading, setLoading, companyDetails }) => {
  const { currency: companyCurrency } = useCompanyCurrency();
  const [plData, setPlData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [drillDownData, setDrillDownData] = useState([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [totalSummary, setTotalSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    grossProfit: 0,
    taxPayable: 0,
    paymentsIn: 0,
    paymentsOut: 0
  });

  // Table sorting and pagination
  const { sortedData, sortConfig, handleSort } = useTableSort(plData, { key: 'category', direction: 'asc' });
  const pagination = useTablePagination(plData, 25);

  // Fetch Profit & Loss data
  useEffect(() => {
    const fetchPLReport = async () => {
      if (!db || !userId || !appId || !companyId) return;
      
      setLoading(true);
      try {
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];

        // Get all sales in date range
        const salesQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills'),
          where('invoiceDate', '>=', startStr),
          where('invoiceDate', '<=', endStr),
          orderBy('invoiceDate', 'desc')
        );

        const salesSnapshot = await getDocs(salesQuery);
        // resolve helpers
        const resolvePartyId = (docData) => {
          const raw = docData.customFields?.party || docData.party || docData.partyId;
          if (raw && parties?.some(p => p.id === raw)) return raw;
          const byName = docData.partyName || docData.firmName || '';
          const match = (parties || []).find(p => (p.firmName || p.partyName) === byName);
          return match?.id || raw || '';
        };
        const resolvePartyName = (pid, docData) => {
          const p = (parties || []).find(pp => pp.id === pid);
          return docData.partyName || p?.firmName || p?.partyName || p?.name || pid || '';
        };

        let sales = salesSnapshot.docs.map(doc => {
          const d = doc.data();
          const pid = resolvePartyId(d);
          return {
            id: doc.id,
            date: d.invoiceDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            partyId: pid,
            invoiceNumber: d.invoiceNumber || d.number || doc.id,
            partyName: resolvePartyName(pid, d)
          };
        });

        // Filter by selected party in JavaScript if needed
        if (selectedParty) {
          sales = sales.filter(sale => sale.partyId === selectedParty);
        }

        // Get all purchases in date range
        const purchasesQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills'),
          where('billDate', '>=', startStr),
          where('billDate', '<=', endStr),
          orderBy('billDate', 'desc')
        );

        const purchasesSnapshot = await getDocs(purchasesQuery);
        let purchases = purchasesSnapshot.docs.map(doc => {
          const d = doc.data();
          const pid = resolvePartyId(d);
          return {
            id: doc.id,
            date: d.billDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            partyId: pid,
            billNumber: d.billNumber || d.number || doc.id,
            partyName: resolvePartyName(pid, d)
          };
        });

        // Filter by selected party in JavaScript if needed
        if (selectedParty) {
          purchases = purchases.filter(purchase => purchase.partyId === selectedParty);
        }

        // Fetch items for GST meta (to exactly match Taxes GSTR-3B)
        const itemsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'items'));
        const itemMeta = {};
        itemsSnap.forEach(d => {
          const it = d.data() || {};
          itemMeta[d.id] = {
            gstPercentage: parseFloat(it.gstPercentage) || 0,
            compositionGstRate: parseFloat(it.compositionGstRate) || 0,
            itemType: it.itemType || 'Goods'
          };
        });

        // Get all expenses in date range
        const expensesQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'),
          where('date', '>=', startStr),
          where('date', '<=', endStr),
          orderBy('date', 'desc')
        );

        const expensesSnapshot = await getDocs(expensesQuery);
        let expenses = expensesSnapshot.docs.map(doc => {
          const d = doc.data();
          const pid = d.partyId || d.party || d.customFields?.party || '';
          const p = (parties || []).find(pp => pp.id === pid);
          const docName = d.expenseNumber || d.voucherNumber || d.voucherNo || d.referenceNo || d.number || d.head || d.category || d.description || doc.id;
          
          // Ensure amount is a valid number
          const amount = parseFloat(d.amount || 0);
          if (isNaN(amount)) {
            console.warn('Invalid expense amount in document:', doc.id, d.amount);
          }
          
          return {
            id: doc.id,
            date: d.date,
            amount: isNaN(amount) ? 0 : amount,
            partyId: pid,
            expenseNumber: docName,
            partyName: d.partyName || p?.firmName || p?.partyName || p?.name || '',
            head: d.head || d.category || d.expenseHead || (d.group === 'salaries' ? 'Salaries' : 'Operating Expenses'),
            group: d.group || ''
          };
        });

        // Salary payments collection
        const salarySnap = await getDocs(query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'),
          where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'asc')
        ));
        const salaryPaymentsRows = salarySnap.docs.map(doc => {
          const d = doc.data();
          const amount = parseFloat(d.netAmount || d.totalEarnings || d.total || 0);
          
          if (isNaN(amount)) {
            console.warn('Invalid salary amount in document:', doc.id, d.netAmount || d.totalEarnings || d.total);
          }
          
          return {
            id: doc.id,
            date: d.date,
            amount: isNaN(amount) ? 0 : amount,
            partyId: d.employeeId || '',
            partyName: d.employeeName || 'Employee',
            head: 'Salaries',
            expenseNumber: d.month ? `${d.month}` : doc.id
          };
        });

        // Irregular labour/freelancer payments (Advances/Bonus/Incentive) - only unsettled
        const irregularSnap = await getDocs(query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'irregularPayments'),
          where('date', '>=', startStr), where('date', '<=', endStr), orderBy('date', 'asc')
        ));
        const irregularRows = irregularSnap.docs.map(doc => {
          const d = doc.data();
          const partyName = d.personType === 'Employee' ? (d.employeeName || 'Employee') : (d.personName || d.personType || 'Labour/Freelancer');
          const amount = parseFloat(d.amount || 0);
          
          if (isNaN(amount)) {
            console.warn('Invalid irregular payment amount in document:', doc.id, d.amount);
          }
          
          return {
            id: doc.id,
            date: d.date,
            amount: isNaN(amount) ? 0 : amount,
            partyId: d.employeeId || '',
            partyName,
            head: 'Labour/Advances',
            expenseNumber: d.paymentType || 'Irregular Payment',
            settled: d.settled || false
          };
          }).filter(r => !r.settled); // Only include unsettled advances

        // Filter by selected party in JavaScript if needed
        if (selectedParty) {
          expenses = expenses.filter(expense => expense.partyId === selectedParty);
        }

        // Fetch payments in the period (for info cards)
        const paymentsQueryRef = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'payments'),
          where('paymentDate', '>=', startStr),
          where('paymentDate', '<=', endStr),
          orderBy('paymentDate', 'asc')
        );
        const paymentsSnapshot = await getDocs(paymentsQueryRef);
        const payments = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Calculate P&L categories
        const plCategories = [];

        // Get sales returns
        const salesReturnsQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'salesReturns'),
          where('invoiceDate', '>=', startStr),
          where('invoiceDate', '<=', endStr),
          orderBy('invoiceDate', 'desc')
        );
        const salesReturnsSnapshot = await getDocs(salesReturnsQuery);
        const salesReturns = salesReturnsSnapshot.docs.map(doc => {
          const d = doc.data();
          const pid = resolvePartyId(d);
          return {
            id: doc.id,
            date: d.invoiceDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            partyId: pid,
            invoiceNumber: d.number || doc.id,
            partyName: resolvePartyName(pid, d)
          };
        });

        // Get purchase returns
        const purchaseReturnsQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseReturns'),
          where('billDate', '>=', startStr),
          where('billDate', '<=', endStr),
          orderBy('billDate', 'desc')
        );
        const purchaseReturnsSnapshot = await getDocs(purchaseReturnsQuery);
        const purchaseReturns = purchaseReturnsSnapshot.docs.map(doc => {
          const d = doc.data();
          const pid = resolvePartyId(d);
          return {
            id: doc.id,
            date: d.billDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            partyId: pid,
            billNumber: d.number || doc.id,
            partyName: resolvePartyName(pid, d)
          };
        });

        // Income categories (gross invoice values minus returns)
        const totalSales = Math.round(sales.reduce((sum, sale) => sum + (parseFloat(sale.totalAmount) || 0), 0) * 100) / 100;
        const totalSalesReturns = Math.round(salesReturns.reduce((sum, ret) => sum + (parseFloat(ret.totalAmount) || 0), 0) * 100) / 100;
        const netSales = totalSales - totalSalesReturns;
        plCategories.push({
          category: 'Sales',
          amount: netSales,
          type: 'income',
          drillDownData: sales,
          description: 'Revenue from sales of goods/services (net of returns)'
        });
        if (totalSalesReturns > 0) {
          plCategories.push({
            category: 'Sales Returns',
            amount: -totalSalesReturns,
            type: 'expense',
            drillDownData: salesReturns,
            description: 'Returns from customers'
          });
        }

        // Cost of goods purchase (gross purchase bill values minus returns)
        const totalPurchases = Math.round(purchases.reduce((sum, purchase) => sum + (parseFloat(purchase.totalAmount) || 0), 0) * 100) / 100;
        const totalPurchaseReturns = Math.round(purchaseReturns.reduce((sum, ret) => sum + (parseFloat(ret.totalAmount) || 0), 0) * 100) / 100;
        const netPurchases = totalPurchases - totalPurchaseReturns;
        plCategories.push({
          category: 'Cost of Goods Purchase',
          amount: netPurchases,
          type: 'expense',
          drillDownData: purchases,
          description: 'Direct costs of goods purchased (net of returns)'
        });
        if (totalPurchaseReturns > 0) {
          plCategories.push({
            category: 'Purchase Returns',
            amount: -totalPurchaseReturns,
            type: 'income',
            drillDownData: purchaseReturns,
            description: 'Returns to suppliers'
          });
        }

        // Operating expenses grouped by head/category; include salary payments and irregular payments
        const allExpenseEntries = [
          ...expenses,
          ...salaryPaymentsRows,
          ...irregularRows
        ];
        
        // Debug: Check expense amounts before grouping
        console.log('Expense entries before grouping:', allExpenseEntries.map(e => ({
          head: e.head,
          amount: e.amount,
          amountType: typeof e.amount,
          parsedAmount: parseFloat(e.amount || 0)
        })));
        
        const expensesByHead = allExpenseEntries.reduce((acc, e) => {
          const key = e.head || 'Operating Expenses';
          if (!acc[key]) acc[key] = { amount: 0, items: [] };
          
          // Ensure amount is a valid number and properly rounded
          const validAmount = Math.round((parseFloat(e.amount || 0)) * 100) / 100;
          if (isNaN(validAmount)) {
            console.warn('Invalid expense amount found:', e);
            acc[key].amount += 0;
          } else {
            acc[key].amount += validAmount;
          }
          
          acc[key].items.push(e);
          return acc;
        }, {});
        Object.entries(expensesByHead).forEach(([head, val]) => {
          // Ensure amount is valid and properly rounded
          const validAmount = Math.round((isNaN(val.amount) ? 0 : val.amount) * 100) / 100;
          
          plCategories.push({
            category: head,
            amount: validAmount,
            type: 'expense',
            drillDownData: val.items,
            description: head
          });
        });

        // Instead of recalculating, trace the exact GSTR-3B summary from Taxes (saved in localStorage)
        const gstMode = (companyDetails?.gstinType || companyDetails?.gstType || 'regular').toLowerCase();
        const cacheKey = `gstr3b:${appId}:${userId}:${startStr}:${endStr}:${gstMode}`;
        let traced = null;
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) traced = JSON.parse(raw);
        } catch {}

        let outward = { taxable: 0, cgst: 0, sgst: 0, igst: 0, compTax: 0 };
        let inward = { taxable: 0, cgst: 0, sgst: 0, igst: 0, compTax: 0 };
        if (traced && traced.summary) {
          outward = traced.summary.outward || outward;
          inward = traced.summary.inward || inward;
        }

        let taxPayable = 0;
        if (gstMode === 'regular') {
          const cg = Math.round((outward.cgst - inward.cgst) * 100) / 100;
          const sg = Math.round((outward.sgst - inward.sgst) * 100) / 100;
          const ig = Math.round((outward.igst - inward.igst) * 100) / 100;
          taxPayable = Math.round((Math.max(0, cg) + Math.max(0, sg) + Math.max(0, ig)) * 100) / 100;
        } else {
          // Composition: payable equals outward composition tax; no ITC
          taxPayable = Math.round((outward.compTax || 0) * 100) / 100;
        }

        // Taxes category with drill-down
        const taxesDrillDown = [];
        if (gstMode === 'regular') {
          taxesDrillDown.push(
            { date: null, invoiceNumber: 'Outward GST (CGST+SGST+IGST)', partyName: '', amount: (outward.cgst + outward.sgst + outward.igst) },
            { date: null, invoiceNumber: 'Less: Inward ITC', partyName: '', amount: (inward.cgst + inward.sgst + inward.igst) },
            { date: null, invoiceNumber: 'Net GST Payable', partyName: '', amount: taxPayable }
          );
        } else {
          taxesDrillDown.push(
            { date: null, invoiceNumber: 'Outward Composition Tax', partyName: '', amount: outward.compTax },
            { date: null, invoiceNumber: 'Net Composition Payable', partyName: '', amount: taxPayable }
          );
        }

        plCategories.push({
          category: 'Taxes (GST Payable)',
          amount: taxPayable,
          type: 'expense',
          drillDownData: taxesDrillDown,
          description: gstMode === 'regular' ? 'GST payable = Outward tax − Inward ITC' : 'Composition tax on turnover (no ITC)'
        });

        // Payments info (does not affect P&L totals, informational)
        const paymentsIn = Math.round(payments.filter(p => (p.type || '').toLowerCase() === 'receipt').reduce((s, p) => s + (parseFloat(p.amount || p.totalAmount || 0)), 0) * 100) / 100;
        const paymentsOut = Math.round(payments.filter(p => (p.type || '').toLowerCase() === 'payment').reduce((s, p) => s + (parseFloat(p.amount || p.totalAmount || 0)), 0) * 100) / 100;

        // Calculate totals using plCategories directly
        const income = plCategories.filter(cat => cat.type === 'income').reduce((sum, cat) => sum + (cat.amount || 0), 0);
        const totalExpenses = plCategories.filter(cat => cat.type === 'expense').reduce((sum, cat) => sum + (cat.amount || 0), 0);
        const grossProfit = income - netPurchases;
        // Net Profit should already include Taxes category within totalExpenses.
        // Do NOT subtract taxPayable again, otherwise taxes are double-counted.
        const netProfit = income - totalExpenses;
        
        // Ensure all values are valid numbers and properly rounded to 2 decimal places
        const finalIncome = Math.round((isNaN(income) ? 0 : income) * 100) / 100;
        const finalTotalExpenses = Math.round((isNaN(totalExpenses) ? 0 : totalExpenses) * 100) / 100;
        const finalGrossProfit = Math.round((isNaN(grossProfit) ? 0 : grossProfit) * 100) / 100;
        const finalNetProfit = Math.round((isNaN(netProfit) ? 0 : netProfit) * 100) / 100;
        const finalTaxPayable = Math.round((taxPayable || 0) * 100) / 100;
        const finalPaymentsIn = Math.round((paymentsIn || 0) * 100) / 100;
        const finalPaymentsOut = Math.round((paymentsOut || 0) * 100) / 100;

        // Debug logging to verify calculations
        console.log('P&L Calculations:', {
          income: finalIncome,
          totalExpenses: finalTotalExpenses,
          grossProfit: finalGrossProfit,
          netProfit: finalNetProfit,
          expenseCategories: plCategories.filter(cat => cat.type === 'expense').map(cat => ({ category: cat.category, amount: cat.amount }))
        });

        setPlData(plCategories);

        setTotalSummary({
          totalIncome: finalIncome,
          totalExpenses: finalTotalExpenses,
          grossProfit: finalGrossProfit,
          netProfit: finalNetProfit,
          taxPayable: finalTaxPayable,
          paymentsIn: finalPaymentsIn,
          paymentsOut: finalPaymentsOut
        });

        // Persist lightweight P&L summary for other modules (e.g., Balance Sheet)
        try {
          const cacheKeyPl = `pl:${appId}:${userId}:${startStr}:${endStr}`;
          localStorage.setItem(cacheKeyPl, JSON.stringify({
            totals: { income, purchases: netPurchases, expenses: totalExpenses, taxPayable, netProfit },
            period: { start: startStr, end: endStr },
            savedAt: Date.now()
          }));
          try { window.dispatchEvent(new CustomEvent('pl-updated', { detail: { key: cacheKeyPl } })); } catch {}
        } catch {}

      } catch (error) {
        console.error('Error fetching P&L report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPLReport();
    // Listen for GSTR-3B updates and refetch totals automatically
    const onGstr3bUpdated = () => fetchPLReport();
    window.addEventListener('gstr3b-updated', onGstr3bUpdated);
    return () => window.removeEventListener('gstr3b-updated', onGstr3bUpdated);
  }, [db, userId, appId, dateRange, selectedParty, companyDetails]);

  // Prepare export data for GlobalExportButtons
  const getExportData = () => {
    const completeTableData = [
      // Income Section Header
      { category: 'INCOME', amount: '-' },
      // Income items
      ...plData.filter(item => item.type === 'income').map(item => ({
        category: `  ${item.category}`,
        amount: Math.round((item.amount || 0) * 100) / 100
      })),
      // Total Income
      { category: 'Total Income', amount: Math.round((totalSummary.totalIncome || 0) * 100) / 100 },
      // Empty row
      { category: '', amount: '' },
      // Expenses Section Header
      { category: 'EXPENSES', amount: '-' },
      // Expense items
      ...plData.filter(item => item.type === 'expense').map(item => ({
        category: `  ${item.category}`,
        amount: Math.round((item.amount || 0) * 100) / 100
      })),
      // Total Expenses
      { category: 'Total Expenses', amount: Math.round((totalSummary.totalExpenses || 0) * 100) / 100 },
      // Empty row
      { category: '', amount: '' },
      // Net Profit
      { category: 'Net Profit', amount: Math.round((totalSummary.netProfit || 0) * 100) / 100 }
    ];
    return completeTableData;
  };

  const getExportColumns = () => [
    { key: 'category', label: 'Particulars' },
    { key: 'amount', label: `Amount (${companyCurrency})` }
  ];

  const getReportDetails = () => ({
    'Period': `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`,
    'Total Income': totalSummary.totalIncome,
    'Total Expenses': totalSummary.totalExpenses,
    'Gross Profit': totalSummary.grossProfit,
    'Net Profit': totalSummary.netProfit,
    dateRange
  });

  // Format currency
  const formatCurrency = (amount) => {
    return formatCurrencyIntl(amount, companyCurrency);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN');
  };

  // Handle category click for drill-down
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setDrillDownData(category.drillDownData || []);
    setShowDrillDown(true);
  };

  // Handle back from drill-down
  const handleBackFromDrillDown = () => {
    setShowDrillDown(false);
    setSelectedCategory(null);
    setDrillDownData([]);
  };

  // ESC to close drill-down (LIFO if extended later)
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape' && showDrillDown) {
        handleBackFromDrillDown();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [showDrillDown]);

  // Handle drill-down row click
  const handleDrillDownRowClick = (item) => {
    console.log('Open document:', item.id, item.invoiceNumber || item.billNumber || item.expenseNumber);
    // You can implement navigation logic here
  };





  return (
    <div className="p-3 sm:p-6">
      <ReportHeaderWithExport
        title="Profit & Loss Report"
        subtitle={`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}${selectedParty ? ` | Party: ${parties.find(p => p.id === selectedParty)?.partyName || selectedParty}` : ''}`}
        exportButtons={
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="PROFIT_LOSS"
            title="Profit & Loss Report"
            companyDetails={companyDetails}
            reportDetails={getReportDetails()}
            disabled={plData.length === 0}
          />
        }
      />

      <CollapsibleQuickTotals
        cards={[
          { label: 'Total Income', value: formatCurrency(totalSummary.totalIncome), color: 'green' },
          { label: 'Total Expenses', value: formatCurrency(totalSummary.totalExpenses), color: 'red' },
          { label: 'Gross Profit', value: formatCurrency(totalSummary.grossProfit), color: 'blue' },
          { label: 'Net Profit', value: formatCurrency(totalSummary.netProfit), color: 'purple' },
          { label: 'Tax Payable', value: formatCurrency(totalSummary.taxPayable), color: 'yellow' }
        ]}
      />

      {/* Back Button for Drill-down */}
      {showDrillDown && (
        <div className="mb-4">
          <button
            onClick={handleBackFromDrillDown}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-300"
          >
            ← Back to P&L Summary
          </button>
        </div>
      )}

      {/* P&L Summary Table */}
      {!showDrillDown && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table id="report-table" className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Particulars
                  </th>
                                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Amount ({companyCurrency})
                   </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Income Section */}
                <tr className="bg-green-50">
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800">
                    INCOME
                  </td>
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800 text-right">
                    -
                  </td>
                </tr>
                {plData.filter(item => item.type === 'income').map((item, index) => (
                  <tr 
                    key={`income-${index}`}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleCategoryClick(item)}
                  >
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap break-words text-sm text-gray-900 pl-8">
                      {item.category}
                      {item.drillDownData && item.drillDownData.length > 0 && (
                        <span className="ml-2 text-blue-500 text-xs">(Click to view details)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-green-100">
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800">
                    Total Income
                  </td>
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800 text-right">
                    {formatCurrency(totalSummary.totalIncome)}
                  </td>
                </tr>

                {/* Expenses Section */}
                <tr className="bg-red-50">
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800">
                    EXPENSES
                  </td>
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800 text-right">
                    -
                  </td>
                </tr>
                {plData.filter(item => item.type === 'expense').map((item, index) => (
                  <tr 
                    key={`expense-${index}`}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleCategoryClick(item)}
                  >
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap break-words text-sm text-gray-900 pl-8">
                      {item.category}
                      {item.drillDownData && item.drillDownData.length > 0 && (
                        <span className="ml-2 text-blue-500 text-xs">(Click to view details)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-red-100">
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800">
                    Total Expenses
                  </td>
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800 text-right">
                    {formatCurrency(totalSummary.totalExpenses)}
                  </td>
                </tr>

                {/* Net Profit */}
                <tr className="bg-purple-100">
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-purple-800">
                    Net Profit
                  </td>
                  <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-purple-800 text-right">
                    {formatCurrency(totalSummary.netProfit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Drill-down Table */}
      {showDrillDown && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedCategory?.category} - Details
            </h3>
            <p className="text-sm text-gray-600 mt-1">{selectedCategory?.description}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doc No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {drillDownData.map((item, index) => (
                  <tr 
                    key={item.id || index}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleDrillDownRowClick(item)}
                  >
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900">
                      {item.date ? formatDate(item.date) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600 hover:underline">
                        {item.invoiceNumber || item.billNumber || item.expenseNumber || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900">
                      {item.partyName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(item.totalAmount || item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {plData.length === 0 && !loading && !showDrillDown && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">No P&L data found</div>
          <p className="text-gray-400">Try adjusting your filters or date range</p>
        </div>
      )}
    </div>
  );
};

export default ProfitLossReport; 